package com.elan.edubridge.shared.data.datasource

import com.elan.edubridge.shared.domain.model.AcademyApplicationRequest
import com.elan.edubridge.shared.domain.model.AcceptInvitationResult
import com.elan.edubridge.shared.domain.model.AmendAttendanceRequest
import com.elan.edubridge.shared.domain.model.AssignmentKind
import com.elan.edubridge.shared.domain.model.AttendanceStatus
import com.elan.edubridge.shared.domain.model.AttendanceTransition
import com.elan.edubridge.shared.domain.model.BatchCommandResult
import com.elan.edubridge.shared.domain.model.CalendarEvent
import com.elan.edubridge.shared.domain.model.CalendarEventResult
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.CommandResult
import com.elan.edubridge.shared.domain.model.CommentResult
import com.elan.edubridge.shared.domain.model.ContentReportRequest
import com.elan.edubridge.shared.domain.model.ContentReportResolution
import com.elan.edubridge.shared.domain.model.ContentReportResult
import com.elan.edubridge.shared.domain.model.ContentReportStatus
import com.elan.edubridge.shared.domain.model.ContentReportTargetType
import com.elan.edubridge.shared.domain.model.CreateAcademyEventRequest
import com.elan.edubridge.shared.domain.model.CreateClassRequest
import com.elan.edubridge.shared.domain.model.CreateCommentRequest
import com.elan.edubridge.shared.domain.model.CreateInvitationRequest
import com.elan.edubridge.shared.domain.model.CreateAssignmentRequest
import com.elan.edubridge.shared.domain.model.CreateNoticeRequest
import com.elan.edubridge.shared.domain.model.CreatePersonalEventRequest
import com.elan.edubridge.shared.domain.model.CreatePostAttachmentUploadRequest
import com.elan.edubridge.shared.domain.model.CreatePostRequest
import com.elan.edubridge.shared.domain.model.CreateStudentRequest
import com.elan.edubridge.shared.domain.model.EventScope
import com.elan.edubridge.shared.domain.model.InvitationResult
import com.elan.edubridge.shared.domain.model.InvitationPreviewResult
import com.elan.edubridge.shared.domain.model.InvitationPreviewStatus
import com.elan.edubridge.shared.domain.model.InviteeType
import com.elan.edubridge.shared.domain.model.NoticeData
import com.elan.edubridge.shared.domain.model.NoticeResult
import com.elan.edubridge.shared.domain.model.NotificationSummary
import com.elan.edubridge.shared.domain.model.PostAttachment
import com.elan.edubridge.shared.domain.model.PostAttachmentDownloadTicket
import com.elan.edubridge.shared.domain.model.PostAttachmentUploadTicket
import com.elan.edubridge.shared.domain.model.PostComment
import com.elan.edubridge.shared.domain.model.PostData
import com.elan.edubridge.shared.domain.model.PostResult
import com.elan.edubridge.shared.domain.model.PushDeliverySummary
import com.elan.edubridge.shared.domain.model.QrSession
import com.elan.edubridge.shared.domain.model.QrSessionResult
import com.elan.edubridge.shared.domain.model.QrSessionStatus
import com.elan.edubridge.shared.domain.model.QrToken
import com.elan.edubridge.shared.domain.model.QrTokenResult
import com.elan.edubridge.shared.domain.model.RecordAttendanceRequest
import com.elan.edubridge.shared.domain.model.ScanRejection
import com.elan.edubridge.shared.domain.model.ScanRequest
import com.elan.edubridge.shared.domain.model.ScanResult
import com.elan.edubridge.shared.domain.model.UpdateAcademyEventRequest
import com.elan.edubridge.shared.domain.model.UpdateAcademyRequest
import com.elan.edubridge.shared.domain.model.UpdateAssignmentRequest
import com.elan.edubridge.shared.domain.model.UpdateClassRequest
import com.elan.edubridge.shared.domain.model.UpdateNoticeRequest
import com.elan.edubridge.shared.domain.model.UpdatePersonalEventRequest
import com.elan.edubridge.shared.domain.model.UpdatePostRequest
import com.elan.edubridge.shared.domain.model.UpdateStudentRequest
import com.elan.edubridge.shared.domain.model.UserRole
import com.elan.edubridge.shared.domain.policy.PermissionPolicy

/**
 * 데모·테스트용 콜러블 스텁.
 *
 * 실제 검증은 전부 서버에 있다(§3.4) — 이 구현은 화면이 결과 4종(성공/만료/중복/쿨다운)을
 * 그릴 수 있게 하는 **테스트 더블**이고 보안 판정의 근거가 아니다.
 * 진짜 규칙 중 [AttendanceTransition]만 공유해 토글 표시가 서버와 어긋나지 않게 한다.
 */
class InMemoryAcademyCommandDataSource : AcademyCommandDataSource {
    private var sequence = 0
    private val sessions = mutableMapOf<String, QrSession>()
    private val activeNonces = mutableMapOf<String, String>()
    /** 소비된 nonce → 그때 돌려준 결과. 서버처럼 재스캔에 직전 결과를 되돌려준다. */
    private val consumedNonces = mutableMapOf<String, ScanResult>()
    private val revokedLinks = mutableSetOf<String>()
    /** 발급된 초대 코드 -> 발급 요청. 수락이 여기서 학원·대상을 되찾는다. */
    private val invitations = mutableMapOf<String, CreateInvitationRequest>()
    /** 이미 수락된 코드. 서버처럼 같은 코드의 재수락에 `replayed` 성공을 돌려준다. */
    private val acceptedInvitations = mutableSetOf<String>()
    private val statusByStudent = mutableMapOf<String, AttendanceStatus>()
    /** 소프트 삭제된 공지·게시글·댓글의 키. 두 번째 삭제는 서버처럼 FAILED_PRECONDITION이다. */
    private val deletedContent = mutableSetOf<String>()
    /** 게시글별 첨부 예약. 개수 상한과 열람 URL 발급이 여기를 본다. */
    private val attachmentsByPost = mutableMapOf<String, MutableList<PostAttachment>>()
    /** 한 인메모리 호출자를 기준으로 한 대상별 신고 상태. 실제 서버의 신고자별 결정적 id를 흉내 낸다. */
    private val reportIdsByTarget = mutableMapOf<String, String>()
    private val reportStatusById = mutableMapOf<String, ContentReportStatus>()
    private var deliveredCount = 0

    override suspend fun submitAcademyApplication(
        request: AcademyApplicationRequest,
    ): CommandResult = CommandResult(entityId = nextId("application"))

    override suspend fun approveAcademyApplication(applicationId: String): CommandResult =
        CommandResult(entityId = applicationId)

    override suspend fun updateAcademy(request: UpdateAcademyRequest): CommandResult =
        CommandResult(entityId = request.academyId)

    override suspend fun createClass(request: CreateClassRequest): CommandResult =
        CommandResult(entityId = nextId("class"))

    override suspend fun updateClass(request: UpdateClassRequest): CommandResult =
        CommandResult(entityId = request.classId)

    override suspend fun createStudent(request: CreateStudentRequest): CommandResult =
        CommandResult(entityId = nextId("student"))

    override suspend fun updateStudent(request: UpdateStudentRequest): CommandResult =
        CommandResult(entityId = request.studentId)

    override suspend fun createEnrollment(
        academyId: String,
        classId: String,
        studentId: String,
    ): CommandResult = CommandResult(entityId = nextId("enrollment"))

    /**
     * 서버는 선생님 초대에 `targetId`를 금지하고 학생·보호자 초대에는 요구한다(`invitations.ts`).
     * 초대 화면이 이 거부 문구를 그릴 수 있어야 해서 이 한 갈래만 흉내 낸다 — 나머지 검증
     * (권한·중복 연결·만료)은 전부 서버에 있다.
     */
    override suspend fun createInvitation(request: CreateInvitationRequest): InvitationResult {
        val requiresTarget = request.inviteeType != InviteeType.TEACHER
        if (requiresTarget != (request.targetId != null)) {
            return InvitationResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = if (requiresTarget) {
                    "학생·보호자 초대에는 대상 학생이 필요합니다."
                } else {
                    "선생님 초대에는 대상 학생을 지정하지 않습니다."
                },
            )
        }
        val code = normalizeInvitationCode(nextId("invite"))
        invitations[code] = request
        return InvitationResult(
            code = code,
            academyId = request.academyId,
            inviteeType = request.inviteeType,
            targetId = request.targetId,
            // 인메모리에는 시계가 없어 서버가 정하는 만료를 흉내 낼 수 없다.
            expiresAt = DEMO_INVITATION_EXPIRES_AT,
        )
    }

    override suspend fun previewInvitation(invitationCode: String): InvitationPreviewResult {
        val code = normalizeInvitationCode(invitationCode)
        val request = invitations[code] ?: return InvitationPreviewResult(
            status = InvitationPreviewStatus.UNUSABLE,
        )
        return InvitationPreviewResult(
            status = if (code in acceptedInvitations) {
                InvitationPreviewStatus.ALREADY_ACCEPTED_BY_CALLER
            } else {
                InvitationPreviewStatus.USABLE
            },
            academyId = request.academyId,
            academyName = DEMO_ACADEMY_NAME,
            inviteeType = request.inviteeType,
            expiresAt = DEMO_INVITATION_EXPIRES_AT,
        )
    }

    override suspend fun acceptInvitation(invitationCode: String): AcceptInvitationResult {
        val code = normalizeInvitationCode(invitationCode)
        val request = invitations[code]
            ?: return AcceptInvitationResult(
                failure = CommandFailure.NOT_FOUND,
                errorMessage = "초대 코드를 찾을 수 없습니다.",
            )
        val role = when (request.inviteeType) {
            InviteeType.STUDENT -> UserRole.STUDENT
            InviteeType.TEACHER -> UserRole.TEACHER
            // 보호자는 멤버가 아니라 guardianLinks로만 연결되므로 역할·권한이 없다(§2.6).
            InviteeType.GUARDIAN -> null
        }
        return AcceptInvitationResult(
            academyId = request.academyId,
            academyName = DEMO_ACADEMY_NAME,
            inviteeType = request.inviteeType,
            role = role,
            permission = role?.let { PermissionPolicy.permissionFor(it) },
            studentId = request.targetId,
            guardianLinkId = if (request.inviteeType == InviteeType.GUARDIAN) nextId("guardian-link") else null,
            // 단일 사용이지만 재시도는 안전하다 — 두 번째부터는 replayed 성공이다.
            replayed = !acceptedInvitations.add(code),
        )
    }

    override suspend fun assignTeacherToClass(
        academyId: String,
        classId: String,
        teacherUserId: String,
    ): CommandResult = CommandResult(entityId = classId)

    override suspend fun revokeGuardianLink(academyId: String, linkId: String): CommandResult {
        val key = "$academyId/$linkId"
        if (linkId.isBlank()) {
            return CommandResult(
                failure = CommandFailure.NOT_FOUND,
                errorMessage = "보호자 연결을 찾을 수 없습니다.",
            )
        }
        if (!revokedLinks.add(key)) {
            return CommandResult(
                failure = CommandFailure.FAILED_PRECONDITION,
                errorMessage = "이미 해지된 연결입니다.",
            )
        }
        return CommandResult(entityId = linkId)
    }

    override suspend fun recordAttendance(request: RecordAttendanceRequest): CommandResult {
        statusByStudent[studentKey(request.academyId, request.studentId)] = request.status
        return CommandResult(
            entityId = "${request.serviceDate}_${request.classId}_${request.studentId}",
        )
    }

    override suspend fun recordAttendanceBatch(
        requests: List<RecordAttendanceRequest>,
    ): BatchCommandResult = BatchCommandResult(results = requests.map { recordAttendance(it) })

    override suspend fun amendAttendance(request: AmendAttendanceRequest): CommandResult =
        CommandResult(entityId = nextId("event"))

    override suspend fun createQrSession(academyId: String, label: String): QrSessionResult {
        val session = QrSession(
            sessionId = nextId("qr-session"),
            academyId = academyId,
            label = label,
            status = QrSessionStatus.ACTIVE,
        )
        sessions[session.sessionId] = session
        return QrSessionResult(session = session, token = issueToken(session))
    }

    override suspend fun rotateQrToken(academyId: String, sessionId: String): QrTokenResult {
        val session = sessions[sessionId]
            ?: return QrTokenResult(
                failure = CommandFailure.NOT_FOUND,
                errorMessage = "종료된 QR 세션이에요. 다시 열어 주세요.",
            )
        if (session.status != QrSessionStatus.ACTIVE) {
            return QrTokenResult(
                failure = CommandFailure.FAILED_PRECONDITION,
                errorMessage = "종료된 QR 세션이에요. 다시 열어 주세요.",
            )
        }
        return QrTokenResult(token = issueToken(session))
    }

    override suspend fun scanAttendance(request: ScanRequest): ScanResult {
        val session = sessions[request.sessionId]
        // 서버는 "세션 없음"과 "세션 종료"를 다른 rejection으로 구분한다(§3.4-2).
        if (session == null) {
            return ScanResult(
                rejection = ScanRejection.SESSION_NOT_FOUND,
                message = "QR 세션을 찾을 수 없어요.",
            )
        }
        if (session.status != QrSessionStatus.ACTIVE) {
            return ScanResult(
                rejection = ScanRejection.SESSION_ENDED,
                message = "지금은 출결을 받고 있지 않아요.",
            )
        }
        if (activeNonces[request.sessionId] != request.nonce) {
            return ScanResult(
                rejection = ScanRejection.EXPIRED,
                message = "QR 코드가 만료되었어요. 화면을 다시 스캔해 주세요.",
            )
        }

        // 재스캔은 **실패가 아니라 멱등 성공**이다 — 서버가 직전 결과를 그대로 돌려준다.
        val nonceKey = "${request.sessionId}:${request.nonce}"
        consumedNonces[nonceKey]?.let { return it }

        val key = studentKey(request.academyId, DEMO_STUDENT_ID)
        val next = AttendanceTransition.nextOnScan(statusByStudent[key], isLate = false)
        statusByStudent[key] = next
        deliveredCount += 1
        val result = ScanResult(
            eventId = nextId("event"),
            status = next,
            studentName = DEMO_STUDENT_NAME,
            className = session.label,
            occurredAt = request.clientScannedAt,
            message = "${next.displayName} 처리되었어요.",
        )
        consumedNonces[nonceKey] = result
        return result
    }

    override suspend fun endQrSession(academyId: String, sessionId: String): CommandResult {
        val session = sessions[sessionId]
            ?: return CommandResult(
                failure = CommandFailure.NOT_FOUND,
                errorMessage = "QR 세션을 찾을 수 없어요.",
            )
        sessions[sessionId] = session.copy(status = QrSessionStatus.ENDED)
        activeNonces.remove(sessionId)
        return CommandResult(entityId = sessionId)
    }

    // ---- 공지·게시판 ----
    //
    // 서버 검증 중 **작성 폼이 실제로 그려야 하는 거부**만 흉내 낸다: 길이 위반, 빈 수정,
    // 이미 삭제된 대상, 첨부 개수 초과. 권한 판정은 전부 서버에 있다.

    override suspend fun createNotice(request: CreateNoticeRequest): NoticeResult {
        lengthErrorOrNull(request.title, request.content, CONTENT_MAX_LENGTH)?.let {
            return NoticeResult(failure = CommandFailure.INVALID_ARGUMENT, errorMessage = it)
        }
        return NoticeResult(
            notice = NoticeData(
                id = nextId("notice"),
                academyId = request.academyId,
                title = request.title,
                // 인메모리에는 시계가 없어 발행 시각을 만들 수 없다(QrToken.expiresAt과 같은 처리).
                date = "",
                content = request.content,
                contentHtml = request.contentHtml,
                authorUid = DEMO_AUTHOR_UID,
                authorName = DEMO_AUTHOR_NAME,
            ),
            notified = PushDeliverySummary(),
        )
    }

    // ---- 과제 ----
    //
    // 공지와 같은 수준의 스텁이다: 폼이 실제로 그려야 하는 거부(제목 길이, 문제 은행 누락,
    // 빈 수정)만 흉내 내고 나머지는 성공으로 돌려준다. 만들어진 과제가 목록에 다시 나타나지
    // 않는 것도 `createNotice`와 같다 — 인메모리 읽기는 고정된 데모 데이터다.

    override suspend fun createAssignment(request: CreateAssignmentRequest): CommandResult {
        if (request.title.isBlank() || request.title.length > TITLE_MAX_LENGTH) {
            return CommandResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "제목은 1~${TITLE_MAX_LENGTH}자여야 합니다.",
            )
        }
        if (request.kind == AssignmentKind.PROBLEM_BANK && request.problemBank == null) {
            return CommandResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "문제 은행 과제는 출제 조건이 필요합니다.",
            )
        }
        return CommandResult(entityId = nextId("assignment"))
    }

    override suspend fun updateAssignment(request: UpdateAssignmentRequest): CommandResult = when {
        request.title == null && request.dueDate == null -> CommandResult(
            failure = CommandFailure.INVALID_ARGUMENT,
            errorMessage = "변경할 항목이 없습니다.",
        )
        request.title != null && (request.title.isBlank() || request.title.length > TITLE_MAX_LENGTH) ->
            CommandResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "제목은 1~${TITLE_MAX_LENGTH}자여야 합니다.",
            )
        else -> CommandResult(entityId = request.assignmentId)
    }

    override suspend fun deleteAssignment(academyId: String, assignmentId: String): CommandResult =
        CommandResult(entityId = assignmentId)

    override suspend fun updateNotice(request: UpdateNoticeRequest): CommandResult =
        applyPatch(
            key = contentKey(request.academyId, "notice", request.noticeId),
            entityId = request.noticeId,
            title = request.title,
            content = request.content,
            contentMaxLength = CONTENT_MAX_LENGTH,
            missingMessage = "이미 삭제된 공지입니다.",
        )

    override suspend fun deleteNotice(academyId: String, noticeId: String): CommandResult =
        softDelete(contentKey(academyId, "notice", noticeId), noticeId, "이미 삭제된 공지입니다.")

    override suspend fun createPost(request: CreatePostRequest): PostResult {
        lengthErrorOrNull(request.title, request.content, CONTENT_MAX_LENGTH)?.let {
            return PostResult(failure = CommandFailure.INVALID_ARGUMENT, errorMessage = it)
        }
        return PostResult(
            post = PostData(
                id = nextId("post"),
                academyId = request.academyId,
                title = request.title,
                content = request.content,
                authorUid = DEMO_AUTHOR_UID,
                authorName = DEMO_AUTHOR_NAME,
            ),
        )
    }

    override suspend fun updatePost(request: UpdatePostRequest): CommandResult =
        applyPatch(
            key = contentKey(request.academyId, "post", request.postId),
            entityId = request.postId,
            title = request.title,
            content = request.content,
            contentMaxLength = CONTENT_MAX_LENGTH,
            missingMessage = "이미 삭제된 게시글입니다.",
        )

    override suspend fun deletePost(academyId: String, postId: String): CommandResult =
        softDelete(contentKey(academyId, "post", postId), postId, "이미 삭제된 게시글입니다.")

    override suspend fun createComment(request: CreateCommentRequest): CommentResult {
        lengthErrorOrNull(null, request.content, COMMENT_MAX_LENGTH)?.let {
            return CommentResult(failure = CommandFailure.INVALID_ARGUMENT, errorMessage = it)
        }
        if (contentKey(request.academyId, "post", request.postId) in deletedContent) {
            return CommentResult(
                failure = CommandFailure.FAILED_PRECONDITION,
                errorMessage = "삭제된 게시글에는 댓글을 쓸 수 없습니다.",
            )
        }
        return CommentResult(
            comment = PostComment(
                id = nextId("comment"),
                postId = request.postId,
                academyId = request.academyId,
                content = request.content,
                authorUid = DEMO_AUTHOR_UID,
                authorName = DEMO_AUTHOR_NAME,
            ),
            notified = PushDeliverySummary(),
        )
    }

    override suspend fun deleteComment(
        academyId: String,
        postId: String,
        commentId: String,
    ): CommandResult =
        softDelete(contentKey(academyId, "comment", "$postId/$commentId"), commentId, "이미 삭제된 댓글입니다.")

    /**
     * 서명 URL은 만들 수 없으므로 오브젝트 경로를 그대로 되돌려준다 — 화면이 티켓 필드를 읽고
     * PUT을 조립하는 흐름까지는 검증되지만 실제 업로드는 되지 않는다.
     */
    override suspend fun createPostAttachmentUploadUrl(
        request: CreatePostAttachmentUploadRequest,
    ): PostAttachmentUploadTicket {
        if (request.fileName.isBlank() || request.fileName.length > FILE_NAME_MAX_LENGTH) {
            return PostAttachmentUploadTicket(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "파일 이름은 1~${FILE_NAME_MAX_LENGTH}자여야 합니다.",
            )
        }
        if (request.sizeBytes !in 1..ATTACHMENT_MAX_BYTES) {
            return PostAttachmentUploadTicket(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "첨부 파일은 10MB까지 올릴 수 있습니다.",
            )
        }
        val postKey = contentKey(request.academyId, "post", request.postId)
        val reserved = attachmentsByPost.getOrPut(postKey) { mutableListOf() }
        if (reserved.size >= MAX_ATTACHMENTS_PER_POST) {
            return PostAttachmentUploadTicket(
                failure = CommandFailure.FAILED_PRECONDITION,
                errorMessage = "첨부 파일은 게시글당 ${MAX_ATTACHMENTS_PER_POST}개까지 올릴 수 있습니다.",
            )
        }
        val attachmentId = nextId("attachment")
        val objectPath = attachmentObjectPath(request.academyId, request.postId, attachmentId)
        reserved += PostAttachment(
            id = attachmentId,
            fileName = request.fileName,
            contentType = request.contentType,
            sizeBytes = request.sizeBytes,
            objectPath = objectPath,
        )
        return PostAttachmentUploadTicket(
            attachmentId = attachmentId,
            academyId = request.academyId,
            postId = request.postId,
            fileName = request.fileName,
            contentType = request.contentType,
            sizeBytes = request.sizeBytes,
            objectPath = objectPath,
            uploadUrl = objectPath,
            requiredHeaders = mapOf("Content-Type" to request.contentType.mimeType),
            expiresAt = "",
        )
    }

    override suspend fun getPostAttachmentUrl(
        academyId: String,
        postId: String,
        attachmentId: String,
    ): PostAttachmentDownloadTicket {
        val attachment = attachmentsByPost[contentKey(academyId, "post", postId)]
            ?.firstOrNull { it.id == attachmentId }
            ?: return PostAttachmentDownloadTicket(
                failure = CommandFailure.NOT_FOUND,
                errorMessage = "첨부 파일을 찾을 수 없습니다.",
            )
        return PostAttachmentDownloadTicket(
            attachmentId = attachment.id,
            academyId = academyId,
            postId = postId,
            fileName = attachment.fileName,
            contentType = attachment.contentType,
            sizeBytes = attachment.sizeBytes,
            objectPath = attachment.objectPath,
            downloadUrl = attachment.objectPath,
            expiresAt = "",
        )
    }

    override suspend fun reportContent(request: ContentReportRequest): ContentReportResult {
        if (request.targetId.isBlank() ||
            (request.targetType == ContentReportTargetType.COMMENT && request.postId.isNullOrBlank())
        ) {
            return ContentReportResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "신고 대상을 확인해 주세요.",
            )
        }
        val targetKey = "${request.academyId}/${request.targetType}/${request.targetId}"
        val existingId = reportIdsByTarget[targetKey]
        if (existingId != null) {
            return when (reportStatusById[existingId]) {
                ContentReportStatus.OPEN -> ContentReportResult(
                    reportId = existingId,
                    alreadyReported = true,
                )
                ContentReportStatus.RESOLVED -> {
                    reportStatusById[existingId] = ContentReportStatus.OPEN
                    ContentReportResult(reportId = existingId, reopened = true)
                }
                null -> ContentReportResult(
                    failure = CommandFailure.INTERNAL,
                    errorMessage = "신고 상태를 확인하지 못했어요.",
                )
            }
        }
        val reportId = nextId("report")
        reportIdsByTarget[targetKey] = reportId
        reportStatusById[reportId] = ContentReportStatus.OPEN
        return ContentReportResult(reportId = reportId)
    }

    override suspend fun resolveContentReport(
        academyId: String,
        reportId: String,
        resolution: ContentReportResolution,
        note: String?,
    ): CommandResult {
        if (reportStatusById[reportId] == null) {
            return CommandResult(
                failure = CommandFailure.NOT_FOUND,
                errorMessage = "신고를 찾을 수 없습니다.",
            )
        }
        if (reportStatusById[reportId] != ContentReportStatus.OPEN) {
            return CommandResult(
                failure = CommandFailure.FAILED_PRECONDITION,
                errorMessage = "이미 처리된 신고입니다.",
            )
        }
        reportStatusById[reportId] = ContentReportStatus.RESOLVED
        return CommandResult(entityId = reportId)
    }

    // ---- 일정 ----
    //
    // 공지·게시판과 같은 방침이다: 작성 폼이 실제로 그려야 하는 거부만 흉내 낸다
    // (길이 위반, 시각 역전, 빈 수정, 이미 삭제된 대상). EDITOR 판정은 서버에만 있다.

    override suspend fun createAcademyEvent(request: CreateAcademyEventRequest): CalendarEventResult {
        eventErrorOrNull(request.title, request.description, request.startAt, request.endAt)?.let {
            return CalendarEventResult(failure = CommandFailure.INVALID_ARGUMENT, errorMessage = it)
        }
        return CalendarEventResult(
            event = CalendarEvent(
                id = nextId("event"),
                scope = EventScope.ACADEMY,
                academyId = request.academyId,
                title = request.title,
                description = request.description,
                startAt = request.startAt,
                endAt = request.endAt,
                allDay = request.allDay,
                createdBy = DEMO_AUTHOR_UID,
                createdByName = DEMO_AUTHOR_NAME,
            ),
        )
    }

    override suspend fun updateAcademyEvent(request: UpdateAcademyEventRequest): CommandResult =
        applyEventPatch(
            key = contentKey(request.academyId, "event", request.eventId),
            entityId = request.eventId,
            title = request.title,
            description = request.description,
            startAt = request.startAt,
            endAt = request.endAt,
            allDay = request.allDay,
        )

    override suspend fun deleteAcademyEvent(academyId: String, eventId: String): CommandResult =
        softDelete(contentKey(academyId, "event", eventId), eventId, "이미 삭제된 일정입니다.")

    override suspend fun createPersonalEvent(request: CreatePersonalEventRequest): CalendarEventResult {
        eventErrorOrNull(request.title, request.description, request.startAt, request.endAt)?.let {
            return CalendarEventResult(failure = CommandFailure.INVALID_ARGUMENT, errorMessage = it)
        }
        return CalendarEventResult(
            event = CalendarEvent(
                id = nextId("event"),
                scope = EventScope.PERSONAL,
                title = request.title,
                description = request.description,
                startAt = request.startAt,
                endAt = request.endAt,
                allDay = request.allDay,
                createdBy = DEMO_AUTHOR_UID,
            ),
        )
    }

    /** 개인 일정에는 `academyId`가 없으므로 소프트 삭제 키의 학원 자리에 소유자 표식을 쓴다. */
    override suspend fun updatePersonalEvent(request: UpdatePersonalEventRequest): CommandResult =
        applyEventPatch(
            key = contentKey(PERSONAL_CALENDAR_KEY, "event", request.eventId),
            entityId = request.eventId,
            title = request.title,
            description = request.description,
            startAt = request.startAt,
            endAt = request.endAt,
            allDay = request.allDay,
        )

    override suspend fun deletePersonalEvent(eventId: String): CommandResult =
        softDelete(
            contentKey(PERSONAL_CALENDAR_KEY, "event", eventId),
            eventId,
            "이미 삭제된 일정입니다.",
        )

    /**
     * 서버 `getNotificationSummary`는 최근 7일 집계 {delivered, retried, failed}를 돌려준다.
     * 인메모리에는 발송 전송로가 없으므로 실제로 처리한 스캔 수만 delivered로 세고
     * 나머지는 0으로 둔다 — 고정된 데모 숫자(71 · 2)는 서버 계약과 무관한 값이었다.
     */
    override suspend fun notificationSummary(academyId: String): NotificationSummary =
        NotificationSummary(
            academyId = academyId,
            deliveredCount = deliveredCount,
            retryingCount = 0,
            failedCount = 0,
        )

    private fun issueToken(session: QrSession): QrToken {
        val nonce = nextId("nonce")
        activeNonces[session.sessionId] = nonce
        return QrToken(
            nonce = nonce,
            expiresAt = "",
            rotationSeconds = session.rotationSeconds,
        )
    }

    private fun nextId(prefix: String): String = "$prefix-${++sequence}"

    private fun contentKey(academyId: String, kind: String, id: String): String = "$academyId/$kind/$id"

    /** 서버 `requireString(minLength, maxLength)`와 같은 갈래. [title]이 null이면 댓글(제목 없음)이다. */
    private fun lengthErrorOrNull(title: String?, content: String, contentMaxLength: Int): String? = when {
        title != null && (title.isBlank() || title.length > TITLE_MAX_LENGTH) ->
            "제목은 1~${TITLE_MAX_LENGTH}자여야 합니다."
        content.isBlank() || content.length > contentMaxLength ->
            "내용은 1~${contentMaxLength}자여야 합니다."
        else -> null
    }

    /** 공지·게시글 수정의 공통부. 서버처럼 빈 수정과 툼스톤 수정을 각각 거부한다. */
    private fun applyPatch(
        key: String,
        entityId: String,
        title: String?,
        content: String?,
        contentMaxLength: Int,
        missingMessage: String,
    ): CommandResult {
        if (title == null && content == null) {
            return CommandResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "변경할 항목이 없습니다.",
            )
        }
        if (key in deletedContent) {
            return CommandResult(failure = CommandFailure.FAILED_PRECONDITION, errorMessage = missingMessage)
        }
        if (title != null && (title.isBlank() || title.length > TITLE_MAX_LENGTH)) {
            return CommandResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "제목은 1~${TITLE_MAX_LENGTH}자여야 합니다.",
            )
        }
        if (content != null && (content.isBlank() || content.length > contentMaxLength)) {
            return CommandResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "내용은 1~${contentMaxLength}자여야 합니다.",
            )
        }
        return CommandResult(entityId = entityId)
    }

    /**
     * 일정 등록의 공통 검증. 설명은 선택이라 빈 문자열을 허용하는 것이 공지·게시글과 다르다.
     *
     * `endAt == startAt`은 통과한다 — 서버와 같이 길이 0인 "시각 표시" 일정을 허용한다.
     * 두 시각을 문자열로 비교하는 것은 서버가 UTC 정규형으로만 저장하기 때문이며, 인메모리는
     * 그 정규화를 흉내 내지 않으므로 화면이 이미 같은 형태로 넘긴다고 가정한다.
     */
    private fun eventErrorOrNull(
        title: String,
        description: String,
        startAt: String,
        endAt: String,
    ): String? = when {
        title.isBlank() || title.length > TITLE_MAX_LENGTH -> "제목은 1~${TITLE_MAX_LENGTH}자여야 합니다."
        description.length > DESCRIPTION_MAX_LENGTH -> "설명은 ${DESCRIPTION_MAX_LENGTH}자 이하여야 합니다."
        startAt.isBlank() || endAt.isBlank() -> "시작·종료 시각이 필요합니다."
        endAt < startAt -> "종료 시각은 시작 시각보다 앞설 수 없습니다."
        else -> null
    }

    /**
     * 일정 수정의 공통부. [applyPatch]와 갈래는 같지만 항목이 다섯이고 설명은 빈 문자열로
     * 지울 수 있어 따로 둔다.
     *
     * 한쪽 시각만 바꿀 때 저장된 반대쪽과 비교하는 검증은 흉내 내지 않는다 — 인메모리에는
     * 원본 문서가 없다. 그 판정은 서버가 트랜잭션 안에서 한다.
     */
    private fun applyEventPatch(
        key: String,
        entityId: String,
        title: String?,
        description: String?,
        startAt: String?,
        endAt: String?,
        allDay: Boolean?,
    ): CommandResult {
        if (title == null && description == null && startAt == null && endAt == null && allDay == null) {
            return CommandResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "변경할 항목이 없습니다.",
            )
        }
        if (key in deletedContent) {
            return CommandResult(
                failure = CommandFailure.FAILED_PRECONDITION,
                errorMessage = "이미 삭제된 일정입니다.",
            )
        }
        if (title != null && (title.isBlank() || title.length > TITLE_MAX_LENGTH)) {
            return CommandResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "제목은 1~${TITLE_MAX_LENGTH}자여야 합니다.",
            )
        }
        if (description != null && description.length > DESCRIPTION_MAX_LENGTH) {
            return CommandResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "설명은 ${DESCRIPTION_MAX_LENGTH}자 이하여야 합니다.",
            )
        }
        if (startAt != null && endAt != null && endAt < startAt) {
            return CommandResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "종료 시각은 시작 시각보다 앞설 수 없습니다.",
            )
        }
        return CommandResult(entityId = entityId)
    }

    private fun softDelete(key: String, entityId: String, alreadyDeletedMessage: String): CommandResult =
        if (deletedContent.add(key)) {
            CommandResult(entityId = entityId)
        } else {
            CommandResult(failure = CommandFailure.FAILED_PRECONDITION, errorMessage = alreadyDeletedMessage)
        }

    /** 서버 `postAttachmentObjectPath`와 같은 레이아웃 — 티켓을 쓰는 쪽이 경로 모양을 확인할 수 있다. */
    private fun attachmentObjectPath(academyId: String, postId: String, attachmentId: String): String =
        "edubridge/academies/$academyId/posts/$postId/$attachmentId"

    /** 서버 `normalizeCode`와 같은 정규화 — 입력 화면이 넘긴 소문자·공백·하이픈을 흡수한다. */
    private fun normalizeInvitationCode(value: String): String =
        value.uppercase().replace(Regex("[\\s-]"), "")

    private fun studentKey(academyId: String, studentId: String): String = "$academyId/$studentId"

    private companion object {
        const val DEMO_STUDENT_ID = "student-1"
        const val DEMO_STUDENT_NAME = "김학생"
        const val DEMO_ACADEMY_NAME = "ABC 수학학원"
        const val DEMO_AUTHOR_UID = "teacher"
        const val DEMO_AUTHOR_NAME = "김선생님"
        const val DEMO_INVITATION_EXPIRES_AT = "2099-01-01T00:00:00Z"

        /** 개인 캘린더에는 학원이 없어 [contentKey]의 학원 자리에 대신 넣는 표식. */
        const val PERSONAL_CALENDAR_KEY = "me"

        // 서버 상한(`notices.ts`/`board.ts`/`events.ts`)과 같은 값이어야 데모에서 미리 걸린다.
        const val TITLE_MAX_LENGTH = 100
        const val CONTENT_MAX_LENGTH = 5000
        const val COMMENT_MAX_LENGTH = 1000
        const val DESCRIPTION_MAX_LENGTH = 2000
        const val FILE_NAME_MAX_LENGTH = 200
        const val ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024
        const val MAX_ATTACHMENTS_PER_POST = 5
    }
}
